import { Telemetry } from '@amzn/ihm-telemetry-js-lib';
import { State } from '../state';
import { Operation, Persistence } from '../persistence';
import { Event } from './_event';

export class Action extends Event {
    private expectedTaskName: string | null;
    private estimatedEndTime: number | null;
    private lastHTTPCallTime: number | null;

    protected save() {
        Persistence.save(Operation.ACTION, this._id, this.toJSON());
    }

    protected static getCurrentAction() {
        const actionId = Persistence.getCurrent(Operation.ACTION);
        return Action.fromJSON(Persistence.getById(Operation.ACTION, actionId));
    }

    protected setCurrent() {
        Persistence._setCurrent(Operation.ACTION, this._id);
    }

    protected delete() {
        Persistence.delete(Operation.ACTION, this._id);
    }

    protected hydrate(data: any) {
        super.hydrate(data);
        this.expectedTaskName = data.expectedTaskName;
        this.estimatedEndTime = data.estimatedEndTime;
        this.lastHTTPCallTime = data.lastHTTPCallTime;
    }

    protected toJSON() {
        return {
            ...super.toJSON(),
            expectedTaskName: this.expectedTaskName,
            estimatedEndTime: this.estimatedEndTime,
            lastHTTPCallTime: this.lastHTTPCallTime,
        };
    }

    private getCurrentTaskName(): string | null {
        try {
            const currentTaskId = Persistence.getCurrent(Operation.TASK);
            if (!currentTaskId) return null;

            const taskData = Persistence.getById(Operation.TASK, currentTaskId);
            return taskData?.name || null;
        } catch (error) {
            console.error('Error getting current task name:', error);
            return null;
        }
    }

    private getCurrentTaskId(): string | null {
        try {
            const currentTaskId = Persistence.getCurrent(Operation.TASK);
            if (!currentTaskId) return null;

            const taskData = Persistence.getById(Operation.TASK, currentTaskId);
            return taskData?.eventId || null;
        } catch (error) {
            console.error('Error getting current task ID:', error);
            return null;
        }
    }

    public get actionName(): string {
        return this.name;
    }

    public get taskName(): string | null {
        return this.expectedTaskName;
    }

    public constructor(name: string, expectedTaskName?: string, save: boolean = true) {
        super(name);
        this.expectedTaskName = expectedTaskName ?? null;
        this.estimatedEndTime = null;
        this.lastHTTPCallTime = null;
        if (save) this.save();
    }

    public static createWithId(id: string, name: string, expectedTaskName?: string, save: boolean = true): Action {
        const action = new Action(name, expectedTaskName, false);
        action._id = id;
        if (save) action.save();
        return action;
    }

    public static fromJSON(data: any) {
        if (!data) return null;
        const action = new Action(data.name, data.expectedTaskName, false);
        action.hydrate(data);
        action.save();
        return action;
    }

    public start() {
        try {
            super.start();
            Action.getCurrentAction()?.stop(true);
            this.setCurrent();
            this.save();
            // TODO: revert to debug once we investigate
            console.info('[Telemetry] Action started successfully with the name: ' + this.name + ', id: ' + this._id);
        } catch (error) {
            console.error('Error starting action:', error);
        }
    }

    public stop(aborted: boolean = false) {
        try {
            if (!this.startTime || this.completed) return;
            super.stop(aborted);
            this.save();
            if (Action.getCurrentAction()?._id === this._id) {
                Persistence._setCurrent(Operation.ACTION, null);
            }
            const data = Event.getPayload(Persistence.getById(Operation.ACTION, this._id));
            if (data) {
                data.taskName = this.getCurrentTaskName();
                data.taskId = this.getCurrentTaskId();
                data.aborted = aborted;
                data.expectedTaskName = data.expectedTaskName ?? 'missing expected Task';
                if (this.duration > 10000 && !data.estimatedEndTime && data.lastHTTPCallTime) {
                    data.estimatedEndTime = data.lastHTTPCallTime;
                    data.errMsg = (data.errMsg ?? '') + 'Action Timeout';
                }
                if (this.duration > 60000) {
                    console.debug('Action Timed Out' + this.name);
                    Telemetry.emit('process.actionTimedOut', data);
                    return;
                }
                if (!aborted) {
                    Telemetry.emit('process.action', data);
                } else {
                    const currentToolId = State.getWorkflow()?.workflowName ?? 'unknown';
                    console.debug('Aborted action' + this.name + 'started by' + data.toolId + 'emitted by' + currentToolId);
                    Telemetry.emit('process.actionAborted', data);
                }
            }
            console.info(
                '[Telemetry] Action stopped successfully with the name: ' + this.name + ', id: ' + this._id,
                +', aborted: ' + aborted.toString() + ', duration: ' + this.duration,
            );
            this.delete();
        } catch (error) {
            console.error('Error stopping action:', error);
        }
    }

    public setLastHTTPCallTime() {
        const timestamp = new Date().getTime();
        if (this.lastHTTPCallTime && !this.estimatedEndTime && timestamp - this.lastHTTPCallTime > 10000) {
            this.estimatedEndTime = this.lastHTTPCallTime;
            this.errMsg = (this.errMsg ?? '') + 'Timeout between HTTP calls';
        }
        this.lastHTTPCallTime = timestamp;
        this.save();
    }
}
